import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
    Loader2, Search, Lightbulb, Clock, FlaskConical, CheckCircle2, XCircle, Rocket, Trophy,
    Tag, TrendingUp, Wrench, UserCog, MessageSquare, Send, Star,
} from 'lucide-react';
import { toast } from 'sonner';
import { ideasAPI } from '@/lib/api';
import { IdeaStatusBadge } from './IdeaStatusBadge';
import {
    IDEA_CATEGORIES, IDEA_STATUSES, STATUS_ORDER, IDEA_IMPACTS, IDEA_EASES,
    CATEGORY_LABELS, IMPACT_LABELS, EASE_LABELS, fmtDate,
} from '@/lib/ideaConstants';
import { cn } from '@/lib/utils';
import { Linkify } from '@/lib/linkify';

const SORTS = [
    { value: 'recent', label: 'Plus récent' },
    { value: 'oldest', label: 'Plus ancien' },
    { value: 'impact', label: 'Plus fort impact' },
    { value: 'ease', label: 'Plus simple à mettre en place' },
];
const ALL = '__all__';

const priorityColor = (p) => p >= 15 ? 'bg-emerald-100 text-emerald-700' : p >= 8 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600';

export const IdeaAdminPanel = ({ refreshKey }) => {
    const [stats, setStats] = useState(null);
    const [items, setItems] = useState(null);
    const [assignable, setAssignable] = useState([]);
    const [selected, setSelected] = useState(null);
    const [filters, setFilters] = useState({ category: '', status: '', impact: '', ease: '', q: '', sort: 'recent', date_from: '', date_to: '' });

    const load = useCallback(async () => {
        const params = {};
        Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
        const [s, l] = await Promise.all([ideasAPI.stats(), ideasAPI.list(params)]);
        setStats(s.data);
        setItems(l.data.items);
    }, [filters]);

    useEffect(() => { load(); }, [load, refreshKey]);
    useEffect(() => { ideasAPI.assignableUsers().then(({ data }) => setAssignable(data)).catch(() => {}); }, []);

    const onChanged = (updated) => {
        setItems((prev) => prev?.map((i) => (i.id === updated.id ? { ...i, ...updated } : i)));
        setSelected((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
        ideasAPI.stats().then(({ data }) => setStats(data)).catch(() => {});
    };

    const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v === ALL ? '' : v }));

    const STAT_CARDS = stats ? [
        { key: 'total', label: 'Total', value: stats.total, icon: Lightbulb, color: 'text-amber-500 bg-amber-50' },
        { key: 'en_attente', label: 'En attente', value: stats.by_status.en_attente, icon: Clock, color: 'text-slate-500 bg-slate-100' },
        { key: 'a_l_etude', label: "À l'étude", value: stats.by_status.a_l_etude, icon: FlaskConical, color: 'text-blue-500 bg-blue-50' },
        { key: 'acceptee', label: 'Acceptées', value: stats.by_status.acceptee, icon: CheckCircle2, color: 'text-emerald-500 bg-emerald-50' },
        { key: 'refusee', label: 'Refusées', value: stats.by_status.refusee, icon: XCircle, color: 'text-red-500 bg-red-50' },
        { key: 'mise_en_oeuvre', label: 'Mises en œuvre', value: stats.by_status.mise_en_oeuvre, icon: Rocket, color: 'text-violet-500 bg-violet-50' },
    ] : [];

    return (
        <div className="space-y-6">
            {/* Tableau de bord */}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6" data-testid="idea-stats">
                {STAT_CARDS.map((c) => (
                    <div key={c.key} className="rounded-2xl border border-slate-200 bg-white p-3.5" data-testid={`idea-stat-${c.key}`}>
                        <span className={cn('flex h-8 w-8 items-center justify-center rounded-lg', c.color)}><c.icon className="h-4 w-4" /></span>
                        <p className="mt-2 text-2xl font-black text-slate-900">{c.value ?? 0}</p>
                        <p className="text-xs text-slate-400">{c.label}</p>
                    </div>
                ))}
            </div>

            {/* Top contributeurs + répartition catégories */}
            {stats && (
                <div className="grid gap-3 lg:grid-cols-2">
                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                        <p className="mb-3 flex items-center gap-1.5 text-sm font-bold text-slate-700"><Trophy className="h-4 w-4 text-amber-500" /> Top contributeurs</p>
                        {stats.top_contributors.length ? (
                            <div className="space-y-2">
                                {stats.top_contributors.map((t, i) => (
                                    <div key={t.author_id} className="flex items-center gap-2 text-sm">
                                        <span className={cn('flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold', i === 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500')}>{i + 1}</span>
                                        <span className="flex-1 text-slate-700">{t.name}</span>
                                        <span className="font-semibold text-slate-500">{t.count} idée{t.count > 1 ? 's' : ''}</span>
                                    </div>
                                ))}
                            </div>
                        ) : <p className="text-sm text-slate-400">Aucune donnée.</p>}
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                        <p className="mb-3 flex items-center gap-1.5 text-sm font-bold text-slate-700"><Tag className="h-4 w-4 text-amber-500" /> Répartition par catégorie</p>
                        {stats.by_category.length ? (
                            <div className="space-y-1.5">
                                {stats.by_category.slice(0, 6).map((c) => {
                                    const pct = stats.total ? Math.round((c.count / stats.total) * 100) : 0;
                                    return (
                                        <div key={c.category} className="text-sm">
                                            <div className="flex justify-between text-xs text-slate-500"><span className="truncate">{c.label}</span><span>{c.count}</span></div>
                                            <div className="mt-0.5 h-1.5 w-full rounded-full bg-slate-100"><div className="h-full rounded-full bg-amber-400" style={{ width: `${pct}%` }} /></div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : <p className="text-sm text-slate-400">Aucune donnée.</p>}
                    </div>
                </div>
            )}

            {/* Filtres */}
            <div className="rounded-2xl border border-slate-200 bg-white p-3" data-testid="idea-filters">
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                    <div className="relative sm:min-w-[180px] sm:flex-1">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <Input value={filters.q} onChange={(e) => setF('q', e.target.value)} placeholder="Rechercher (titre, auteur…)" className="pl-9" data-testid="idea-search" />
                    </div>
                    <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 sm:mx-0 sm:overflow-visible sm:px-0 sm:pb-0">
                        <FilterSelect value={filters.category || ALL} onValueChange={(v) => setF('category', v)} placeholder="Catégorie" testid="filter-category"
                            options={IDEA_CATEGORIES.map((c) => ({ value: c.value, label: c.label }))} />
                        <FilterSelect value={filters.status || ALL} onValueChange={(v) => setF('status', v)} placeholder="Statut" testid="filter-status"
                            options={STATUS_ORDER.map((s) => ({ value: s, label: IDEA_STATUSES[s].label }))} />
                        <FilterSelect value={filters.impact || ALL} onValueChange={(v) => setF('impact', v)} placeholder="Impact" testid="filter-impact" options={IDEA_IMPACTS} />
                        <FilterSelect value={filters.ease || ALL} onValueChange={(v) => setF('ease', v)} placeholder="Facilité" testid="filter-ease" options={IDEA_EASES} />
                        <FilterSelect value={filters.sort} onValueChange={(v) => setF('sort', v)} placeholder="Trier" testid="filter-sort" options={SORTS} allowAll={false} />
                    </div>
                </div>
            </div>

            {/* Liste : cartes (mobile) + tableau (desktop) */}
            {items === null ? (
                <div className="flex justify-center rounded-2xl border border-slate-200 bg-white py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
            ) : items.length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-white py-16 text-center text-sm text-slate-400" data-testid="idea-table-empty">Aucune idée ne correspond à ces filtres.</div>
            ) : (
                <>
                    {/* Cartes (mobile) */}
                    <div className="space-y-3 md:hidden" data-testid="idea-cards-mobile">
                        {items.map((idea) => (
                            <button key={idea.id} onClick={() => setSelected(idea)} data-testid={`idea-card-${idea.id}`}
                                className="w-full rounded-2xl border border-slate-200 bg-white p-4 text-left transition-colors hover:border-amber-300">
                                <div className="flex items-start justify-between gap-2">
                                    <p className="line-clamp-2 flex-1 font-semibold text-slate-800">{idea.title}</p>
                                    {idea.priority ? <span className={cn('shrink-0 rounded-md px-2 py-0.5 text-xs font-bold', priorityColor(idea.priority))}>{idea.priority}</span> : null}
                                </div>
                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                    <IdeaStatusBadge status={idea.status} />
                                    <span className="text-xs text-slate-500">{idea.category_label}</span>
                                </div>
                                <div className="mt-2 flex items-center justify-between text-xs text-slate-400">
                                    <span className="truncate">{idea.author_name}</span>
                                    <span className="shrink-0">{fmtDate(idea.created_at)}</span>
                                </div>
                                <p className="mt-1 text-[11px] text-slate-400">{IMPACT_LABELS[idea.impact_estimate]} · {EASE_LABELS[idea.implementation_ease]}</p>
                            </button>
                        ))}
                    </div>

                    {/* Tableau (desktop) */}
                    <div className="hidden overflow-x-auto rounded-2xl border border-slate-200 bg-white md:block">
                        <Table data-testid="idea-table">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Idée</TableHead>
                                    <TableHead>Auteur</TableHead>
                                    <TableHead>Catégorie</TableHead>
                                    <TableHead>Statut</TableHead>
                                    <TableHead className="text-center">Priorité</TableHead>
                                    <TableHead>Date</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {items.map((idea) => (
                                    <TableRow key={idea.id} onClick={() => setSelected(idea)} className="cursor-pointer" data-testid={`idea-row-${idea.id}`}>
                                        <TableCell className="max-w-[260px]">
                                            <p className="truncate font-semibold text-slate-800">{idea.title}</p>
                                            <p className="text-xs text-slate-400">{IMPACT_LABELS[idea.impact_estimate]} · {EASE_LABELS[idea.implementation_ease]}</p>
                                        </TableCell>
                                        <TableCell className="text-sm text-slate-600">{idea.author_name}</TableCell>
                                        <TableCell className="text-xs text-slate-500">{idea.category_label}</TableCell>
                                        <TableCell><IdeaStatusBadge status={idea.status} /></TableCell>
                                        <TableCell className="text-center">
                                            {idea.priority ? <span className={cn('inline-block rounded-md px-2 py-0.5 text-xs font-bold', priorityColor(idea.priority))}>{idea.priority}</span> : <span className="text-xs text-slate-300">—</span>}
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap text-xs text-slate-400">{fmtDate(idea.created_at)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </>
            )}

            <IdeaAdminDetail idea={selected} onClose={() => setSelected(null)} assignable={assignable} onChanged={onChanged} />
        </div>
    );
};

const FilterSelect = ({ value, onValueChange, placeholder, options, testid, allowAll = true }) => (
    <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger className="w-auto min-w-[130px] shrink-0" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent>
            {allowAll && <SelectItem value={ALL}>{placeholder} : tout</SelectItem>}
            {options.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`${testid}-${o.value}`}>{o.label}</SelectItem>)}
        </SelectContent>
    </Select>
);

// ---------- Détail admin (gestion d'une idée) ----------
const IdeaAdminDetail = ({ idea, onClose, assignable, onChanged }) => {
    const [status, setStatusVal] = useState('');
    const [refusal, setRefusal] = useState('');
    const [impact, setImpact] = useState(0);
    const [ease, setEase] = useState(0);
    const [comment, setComment] = useState('');
    const [busy, setBusy] = useState('');

    useEffect(() => {
        if (idea) {
            setStatusVal(idea.status);
            setRefusal(idea.refusal_reason || '');
            setImpact(idea.impact_score || 0);
            setEase(idea.ease_score || 0);
            setComment('');
        }
    }, [idea]);

    if (!idea) return null;

    const saveStatus = async () => {
        if (status === 'refusee' && !refusal.trim()) { toast.error('Le commentaire de refus est obligatoire.'); return; }
        setBusy('status');
        try {
            const { data } = await ideasAPI.setStatus(idea.id, { status, refusal_reason: refusal });
            onChanged(data); toast.success('Statut mis à jour.');
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur.'); } finally { setBusy(''); }
    };
    const saveScoring = async () => {
        if (!impact || !ease) { toast.error('Notez l\'impact et la facilité (1 à 5).'); return; }
        setBusy('score');
        try { const { data } = await ideasAPI.setScoring(idea.id, impact, ease); onChanged(data); toast.success('Notation enregistrée.'); }
        catch (e) { toast.error(e.response?.data?.detail || 'Erreur.'); } finally { setBusy(''); }
    };
    const saveAssign = async (uid) => {
        setBusy('assign');
        try { const { data } = await ideasAPI.assign(idea.id, uid === ALL ? null : uid); onChanged(data); toast.success('Affectation mise à jour.'); }
        catch (e) { toast.error(e.response?.data?.detail || 'Erreur.'); } finally { setBusy(''); }
    };
    const addComment = async () => {
        if (!comment.trim()) return;
        setBusy('comment');
        try {
            const { data } = await ideasAPI.addComment(idea.id, comment);
            onChanged({ id: idea.id, comments: [...(idea.comments || []), data] });
            setComment(''); toast.success('Commentaire ajouté.');
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur.'); } finally { setBusy(''); }
    };

    const priority = impact && ease ? impact * ease : null;

    return (
        <Dialog open={!!idea} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-2xl p-0 overflow-hidden gap-0" data-testid="idea-admin-detail">
                <DialogTitle className="sr-only">Gestion de l'idée</DialogTitle>
                <div className="flex max-h-[88vh] flex-col">
                    <div className="border-b border-slate-100 px-6 py-4">
                        <IdeaStatusBadge status={idea.status} />
                        <h2 className="mt-2 text-lg font-bold text-slate-900">{idea.title}</h2>
                        <p className="mt-0.5 text-xs text-slate-400">{idea.author_name} · {idea.category_label} · {fmtDate(idea.created_at)}</p>
                    </div>
                    <div className="grid flex-1 gap-5 overflow-y-auto px-6 py-5 md:grid-cols-2">
                        {/* Colonne gauche : contenu */}
                        <div className="space-y-4 text-sm">
                            <Block title="Description"><p className="whitespace-pre-wrap text-slate-600">{idea.description}</p></Block>
                            {idea.benefits && <Block title="Bénéfices attendus"><p className="whitespace-pre-wrap text-slate-600">{idea.benefits}</p></Block>}
                            <div className="flex gap-3">
                                <MiniMeta icon={TrendingUp} label="Impact estimé" value={IMPACT_LABELS[idea.impact_estimate]} />
                                <MiniMeta icon={Wrench} label="Facilité" value={EASE_LABELS[idea.implementation_ease]} />
                            </div>
                            <Block title={`Commentaires (${idea.comments?.length || 0})`}>
                                <div className="space-y-2">
                                    {idea.comments?.map((c) => (
                                        <div key={c.id} className="rounded-xl bg-slate-50 p-2.5">
                                            <p className="text-xs font-semibold text-slate-700">{c.author_name} · {fmtDate(c.created_at)}</p>
                                            <p className="mt-0.5 whitespace-pre-wrap text-sm text-slate-600"><Linkify text={c.content} /></p>
                                        </div>
                                    ))}
                                    <div className="flex gap-2">
                                        <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Ajouter un commentaire de traitement…" data-testid="idea-comment-input"
                                            onKeyDown={(e) => e.key === 'Enter' && addComment()} />
                                        <button onClick={addComment} disabled={busy === 'comment'} data-testid="idea-comment-send" className="flex shrink-0 items-center justify-center rounded-lg bg-slate-900 px-3 text-white hover:bg-slate-800 disabled:opacity-60">
                                            {busy === 'comment' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                        </button>
                                    </div>
                                </div>
                            </Block>
                        </div>

                        {/* Colonne droite : gestion */}
                        <div className="space-y-4">
                            <Block title="Statut">
                                <Select value={status} onValueChange={setStatusVal}>
                                    <SelectTrigger data-testid="idea-status-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>{STATUS_ORDER.map((s) => <SelectItem key={s} value={s} data-testid={`idea-status-opt-${s}`}>{IDEA_STATUSES[s].label}</SelectItem>)}</SelectContent>
                                </Select>
                                {status === 'refusee' && (
                                    <Textarea value={refusal} onChange={(e) => setRefusal(e.target.value)} rows={2} placeholder="Motif du refus (obligatoire)…" className="mt-2" data-testid="idea-refusal-input" />
                                )}
                                <button onClick={saveStatus} disabled={busy === 'status'} data-testid="idea-status-save" className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg bg-amber-500 py-2 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-60">
                                    {busy === 'status' ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Enregistrer le statut
                                </button>
                            </Block>

                            <Block title="Notation interne (Impact × Facilité)">
                                <Stars label="Impact potentiel" value={impact} onChange={setImpact} testid="impact" />
                                <Stars label="Facilité de mise en œuvre" value={ease} onChange={setEase} testid="ease" />
                                <div className="mt-2 flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
                                    <span className="text-xs font-medium text-slate-500">Priorité globale</span>
                                    <span className={cn('rounded-md px-2 py-0.5 text-sm font-black', priority ? priorityColor(priority) : 'text-slate-300')} data-testid="idea-priority-value">{priority ?? '—'}</span>
                                </div>
                                <button onClick={saveScoring} disabled={busy === 'score'} data-testid="idea-scoring-save" className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-slate-200 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">
                                    {busy === 'score' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Star className="h-4 w-4" />} Enregistrer la note
                                </button>
                            </Block>

                            <Block title="Responsable de l'étude">
                                <Select value={idea.assigned_to || ALL} onValueChange={saveAssign}>
                                    <SelectTrigger data-testid="idea-assign-select"><SelectValue placeholder="Affecter à…" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={ALL}>Non affecté</SelectItem>
                                        {assignable.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role === 'vp' ? 'VP' : 'Admin'})</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                {idea.assigned_to_name && <p className="mt-1.5 inline-flex items-center gap-1 text-xs text-slate-500"><UserCog className="h-3 w-3" /> Affectée à {idea.assigned_to_name}</p>}
                            </Block>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

const Block = ({ title, children }) => (
    <div><p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-slate-400">{title}</p>{children}</div>
);
const MiniMeta = ({ icon: Icon, label, value }) => (
    <div className="flex-1 rounded-xl border border-slate-100 p-2"><p className="flex items-center gap-1 text-xs text-slate-400"><Icon className="h-3 w-3" />{label}</p><p className="mt-0.5 text-sm font-semibold text-slate-700">{value || '—'}</p></div>
);
const Stars = ({ label, value, onChange, testid }) => (
    <div className="mb-2">
        <p className="mb-1 text-xs text-slate-500">{label}</p>
        <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => onChange(n)} data-testid={`idea-${testid}-star-${n}`}
                    className={cn('flex h-7 w-7 items-center justify-center rounded-md border text-xs font-bold transition-colors', n <= value ? 'border-amber-400 bg-amber-400 text-white' : 'border-slate-200 text-slate-400 hover:bg-slate-50')}>
                    {n}
                </button>
            ))}
        </div>
    </div>
);

export default IdeaAdminPanel;
